import React, { useContext, useState, useEffect } from "react";
import { Modal, Button, TextInput, Label } from "flowbite-react";
import Swal from "sweetalert2";
import axios from "axios";
import DataTable from "react-data-table-component";
import GlobalContext from "../connections/GlobalContext";
import Head_data from "../components/Head_data";
import engineGif from "../image/engine.gif";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numAmount)) {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(0);
    }
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
    }).format(numAmount);
};

const Vendor = ({ theme }) => {
    const { globalValue } = useContext(GlobalContext);
    
    // Modal states
    const [open, setOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    
    // Form states
    const [mode, setMode] = useState("add");
    const [vendor_id, setVendor_id] = useState("");
    const [vendor_name, setVendor_name] = useState("");
    const [mobile_no, setMobile_no] = useState("");
    const [address, setAddress] = useState("");
    const [gst_no, setGst_no] = useState("");
    const [email_id, setEmail_id] = useState("");
    
    // Data states
    const [vendor, setVendor] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [logData, setLogData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    
    // Payment states
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedLogBillNo, setSelectedLogBillNo] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [payment_mode, setPayment_mode] = useState("");
    const [date, setDate] = useState("");
    
    // UI states
    const [loading, setLoading] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [mobileNoWarning, setMobileNoWarning] = useState("");
    const [error, setError] = useState("");
    const [paymentError, setPaymentError] = useState("");
    const [paymentModeError, setPaymentModeError] = useState("");

    // Filter vendors on search term change
    useEffect(() => {
        const filteredData = vendor.filter((v) =>
            v.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.mobile_no?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFiltered(filteredData);
    }, [searchTerm, vendor]);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Initialize date and permissions
    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toISOString().split("T")[0];
        setDate(formattedDate);

        const position = sessionStorage.getItem("position");
        setIsEditable(position === "superadmin");
    }, []);

    // Fetch vendor data
    const fetchData = async () => {
        setLoading(true);
        try {
            const branch = sessionStorage.getItem("branch");
            const response = await fetch(
                `${globalValue}/vendor?data=${encodeURIComponent(branch)}&position=a`,
                {
                    method: "GET",
                    headers: {
                        "x-access-token": sessionStorage.getItem("token"),
                    },
                }
            );
            const dat = await response.json();
            setVendor(Array.isArray(dat) ? dat : []);
        } catch (error) {
            console.error("Error fetching data:", error);
            setVendor([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle payment click
    const handlePayClick = (row) => {
        const pending = (row.amount || 0) - (row.paid || 0);
        setSelectedRow({ ...row, pending });
        setIsModalOpen(true);
        setPaymentAmount("");
        setPaymentError("");
        setPaymentModeError("");
    };

    const customStyles = {
        table: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                width: "100%",
                border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(226, 232, 240)",
            },
        },
        headCells: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
                color: theme === "dark" ? "white" : "black",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                color: theme === "dark" ? "white" : "black",
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(255, 255, 255)",
            },
        },
        pagination: {
            style: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                color: theme === "dark" ? "white" : "black",
                borderTop: theme === "dark" ? "solid 1px white" : "solid 1px rgb(226, 232, 240)",
            },
            pageButtonsStyle: {
                backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
                color: theme === "dark" ? "white" : "black",
                fill: theme === "dark" ? "white" : "black",
                '&:hover': {
                    backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
                },
            },
        },
    };

    // DataTable columns
    const columns = [
        { name: "S no.", selector: (row, index) => (currentPage - 1) * rowsPerPage + index + 1, width: "80px" },
        { name: "Vendor", selector: row => row.vendor_name ?? "-", width: "200px" },
        { name: "Mobile/WhatsApp No", selector: row => row.mobile_no ?? "-", width: "150px" },
        { name: "Amount", selector: row => formatCurrency(row.amount), width: "130px" },
        { name: "Paid", selector: row => formatCurrency(row.paid), width: "130px" },
        {
            name: "Pending",
            selector: row => {
                const amount = row.amount || 0;
                const paid = row.paid || 0;
                const pending = amount - paid;
                return pending > 0 ? formatCurrency(pending) : "-";
            },
            width: "130px"
        },
        ...(sessionStorage.getItem("position") === "superadmin" || sessionStorage.getItem("position") === "admin"
            ? [{
                name: "Action",
                center: true,
                cell: row => (
                    <div className="flex flex-wrap gap-2 justify-center items-center">
                        <button
                            onClick={() => handleEditVendor(row)}
                            className="p-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-md transition"
                            title="Edit"
                        >
                            <i className="fa fa-edit"></i>
                        </button>
                        <button
                            onClick={() => handledelete(row.id)}
                            className="p-2 text-white bg-yellow-500 hover:bg-yellow-600 rounded-md shadow-md transition"
                            title="Delete"
                        >
                            <i className="fa fa-trash"></i>
                        </button>
                        <button
                            onClick={() => handlePayClick(row)}
                            disabled={(row.amount || 0) - (row.paid || 0) <= 0}
                            className={`px-3 py-1 text-xs font-semibold rounded-md shadow-md transition 
                                ${((row.amount || 0) - (row.paid || 0) <= 0)
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}
                        >
                            Pay
                        </button>
                        <button
                            onClick={() => handleLogClick(row)}
                            className="px-3 py-1 text-xs font-semibold rounded-md shadow-md bg-gray-600 text-white hover:bg-gray-700 transition"
                        >
                            Logs
                        </button>
                    </div>
                ),
            }]
            : [])
    ];

    // Mobile number validation
    const handleMobileNoChange = (e) => {
        const value = e.target.value;
        setMobile_no(value);
        if (!value) {
            setMobileNoWarning("Incomplete mobile number");
        } else {
            setMobileNoWarning("");
        }
    };

    // Download PDF for vendor payment logs
    const handleDownloadLogPDF = () => {
        if (!logData || logData.length === 0) {
            Swal.fire("Warning", "No logs to download", "warning");
            return;
        }

        const doc = new jsPDF();
        const vendorDetails = vendor.find(v => v.id === selectedLogBillNo) || {};

        // Helper for PDF currency formatting
        const formatCurrencyPDF = (amount) => {
            const num = typeof amount === "number" ? amount : parseFloat(amount);
            if (isNaN(num)) return "Rs. 0.00";
            return "Rs. " + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text("Vendor Payment Log Report", 14, 15);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);

        doc.setDrawColor(200);
        doc.line(14, 25, 196, 25);

        // Vendor Details
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(`Vendor: ${vendorDetails.vendor_name || 'N/A'}`, 14, 32);
        doc.setFontSize(10);
        doc.setTextColor(80);
        doc.text(`Mobile: ${vendorDetails.mobile_no || 'N/A'}`, 14, 38);
        doc.text(`Address: ${vendorDetails.address || 'N/A'}`, 14, 44);

        const headers = [["S.No", "Date", "Amount", "Payment Mode"]];
        
        // Sort and map data
        const rows = [...logData]
            .sort((a, b) => {
                const parseDate = (str) => {
                    const [day, month, year] = str.split('-').map(Number);
                    return new Date(year, month - 1, day);
                };
                return parseDate(b[1]) - parseDate(a[1]);
            })
            .map((log, index) => [
                index + 1,
                log[1],
                formatCurrencyPDF(log[2]),
                log[4]
            ]);

        // Calculate total amount
        const totalAmount = logData.reduce((sum, log) => {
            const amount = typeof log[2] === 'number' ? log[2] : parseFloat(log[2]);
            return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

        autoTable(doc, {
            head: headers,
            body: rows,
            foot: [["", "", formatCurrencyPDF(totalAmount), ""]],
            startY: 50,
            styles: { fontSize: 10, cellPadding: 3 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center' },
            columnStyles: {
                0: { halign: 'center' },
                1: { halign: 'center' },
                2: { halign: 'right' },
                3: { halign: 'center' },
            },
            footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold', halign: 'right' },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            showFoot: 'lastPage'
        });

        doc.save(`Vendor_Payment_Log_${vendorDetails.vendor_name || 'Report'}.pdf`);
    };

    // Edit vendor
    const handleEditVendor = (row) => {
        setVendor_name(row.vendor_name || "");
        setMobile_no(row.mobile_no || "");
        setAddress(row.address || "");
        setGst_no(row.gst_no || "");
        setVendor_id(row.id || "");
        setEmail_id(row.email_id || "");
        setMode("edit");
        setOpen(true);
    };

    // Add vendor
    const handlesubmit = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${globalValue}/add_vendor`, {
                vendor_name,
                mobile_no,
                address,
                gst_no,
                email_id,
                branch: sessionStorage.getItem("branch"),
            }, {
                headers: { "x-access-token": sessionStorage.getItem("token") },
            });

            if (response.data === "success") {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Vendor added successfully",
                });
                setOpen(false);
                resetForm();
                fetchData();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Error adding vendor",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error adding vendor",
            });
        } finally {
            setLoading(false);
        }
    };

    // Edit vendor
    const handleedit = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${globalValue}/edit_vendor`, {
                id: vendor_id,
                vendor_name,
                mobile_no,
                address,
                gst_no,
                email_id
            }, {
                headers: { "x-access-token": sessionStorage.getItem("token") },
            });

            if (response.data === "success") {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Vendor edited successfully",
                });
                setOpen(false);
                resetForm();
                fetchData();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Error editing vendor",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error editing vendor",
            });
        } finally {
            setLoading(false);
        }
    };

    // Delete vendor
    const handledelete = async (id) => {
        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: "This action will delete the vendor permanently.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        });

        if (!confirmResult.isConfirmed) return;

        setLoading(true);
        try {
            const response = await axios.post(`${globalValue}/delete_vendor`, { id }, {
                headers: { "x-access-token": sessionStorage.getItem("token") }
            });

            if (response.data === "success") {
                Swal.fire({
                    icon: "success",
                    title: "Deleted!",
                    text: "Vendor deleted successfully",
                });
                fetchData();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Error deleting vendor",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Error deleting vendor",
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setVendor_name("");
        setMobile_no("");
        setAddress("");
        setGst_no("");
        setEmail_id("");
        setVendor_id("");
        setMobileNoWarning("");
        setError("");
        setMode("add");
    };

    // Payment handling
    const handlePaymentSubmit = async () => {
        const amount = parseFloat(paymentAmount);
        const currentPending = selectedRow?.pending || 0;

        if (isNaN(amount) || amount <= 0) {
            setPaymentError("Please enter a valid amount (greater than zero).");
            return;
        }
        if (amount > currentPending) {
            setPaymentError(`Amount cannot exceed the pending balance of ${formatCurrency(currentPending)}.`);
            return;
        }
        if (!payment_mode) {
            setPaymentModeError("Please select a payment mode.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${globalValue}/pay_vendor_amount`, {
                id: selectedRow.id,
                amount_paid: amount,
                payment_mode,
                date
            }, {
                headers: { "x-access-token": sessionStorage.getItem("token") }
            });

            const result = response.data;
            if (result.success === 1) {
                handleCloseModal();
                Swal.fire({
                    icon: 'success',
                    title: 'Payment Successful!',
                    text: result.message || `${formatCurrency(amount)} paid for vendor: ${selectedRow.vendor_name}.`,
                    timer: 2500,
                    showConfirmButton: false,
                });
                fetchData();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Payment Failed',
                    text: result.message || 'Payment could not be processed.',
                });
            }
        } catch (error) {
            console.error("Payment failed:", error);
            const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
            Swal.fire({
                icon: 'error',
                title: 'Payment Error',
                text: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    // Payment logs
    const handleLogClick = async (row) => {
        setSelectedLogBillNo(row.id);
        setIsLogModalOpen(true);
        try {
            const res = await axios.post(`${globalValue}/vendor_payment_log`, {
                id: row.id
            }, {
                headers: { "x-access-token": sessionStorage.getItem("token") }
            });

            if (res.data.success === 1) {
                setLogData(res.data.data || []);
            } else {
                setLogData([]);
                Swal.fire("Error", res.data.message, "error");
            }
        } catch (error) {
            console.error("Error fetching logs:", error);
            setLogData([]);
            Swal.fire("Error", "Could not fetch logs", "error");
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRow(null);
        setPaymentAmount("");
        setPaymentError("");
        setPaymentModeError("");
        setPayment_mode("");
    };

    return (
        <div className={`md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 h-[92vh] flex justify-center items-center box-border`}>
            {loading && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
                    <img src={engineGif} alt="Loading..." className="w-24 h-24" />
                </div>
            )}
            
            <div className="w-[95%] md:w-[98%] h-[95%] bg-white px-4 py-2 rounded-xl shadow-xl overflow-auto">
                <div className="w-full flex justify-between text-gray-600">
                    <Head_data data="Manage Vendor" font="" size="3xl" />
                    {sessionStorage.getItem("position") === "superadmin" && (
                        <button
                            onClick={() => {
                                resetForm();
                                setOpen(true);
                            }}
                            className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 md:mt-2"
                        >
                            Add Vendor
                        </button>
                    )}
                </div>

                <input
                    type="text"
                    placeholder="Search by name, mobile no"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full md:w-1/3 px-4 my-2 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                />

                <div className="py-6">
                    <DataTable
                        columns={columns}
                        data={filtered}
                        customStyles={customStyles}
                        pagination
                        paginationPerPage={rowsPerPage}
                        onChangePage={(page) => setCurrentPage(page)}
                        onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                            setRowsPerPage(currentRowsPerPage);
                            setCurrentPage(currentPage);
                        }}
                        noDataComponent="No vendors found"
                    />
                </div>
            </div>

            {/* Payment Modal */}
            {selectedRow && (
                <Modal show={isModalOpen} onClose={handleCloseModal} size="2xl" popup>
                    <Modal.Header>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Process Payment for Vendor: {selectedRow.vendor_name}
                        </h3>
                    </Modal.Header>
                    <Modal.Body className="p-6">
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                                <p><span className="font-semibold">Vendor:</span> {selectedRow.vendor_name}</p>
                                <p>
                                    <span className="font-semibold">Date:</span>
                                    <input
                                        type="date"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        disabled={!isEditable}
                                        className={`ml-2 border border-gray-300 rounded-md px-2 py-1 ${isEditable ? "focus:ring-2 focus:ring-blue-500" : "opacity-60 cursor-not-allowed"}`}
                                    />
                                </p>
                                <p><span className="font-semibold">Total Amount:</span> {formatCurrency(selectedRow.amount)}</p>
                                <p><span className="font-semibold">Amount Paid:</span> {formatCurrency(selectedRow.paid)}</p>
                                <p><span className="font-semibold text-red-600">Pending: {formatCurrency(selectedRow.pending)}</span></p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="payment-amount" value="Enter Amount to Pay" />
                                <TextInput
                                    id="payment-amount"
                                    type="number"
                                    placeholder={`Enter amount (max ${formatCurrency(selectedRow.pending)})`}
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    color={paymentError ? 'failure' : 'default'}
                                    helperText={paymentError}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="payment-mode" value="Payment Mode" />
                                <select
                                    id="payment-mode"
                                    value={payment_mode}
                                    onChange={(e) => setPayment_mode(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Payment Mode</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Online">Online</option>
                                    <option value="UPI">UPI</option>
                                    <option value="Card">Card</option>
                                </select>
                                {paymentModeError && (
                                    <p className="text-sm text-red-600">{paymentModeError}</p>
                                )}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            onClick={handleCloseModal}
                            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handlePaymentSubmit}
                            disabled={!paymentAmount || !payment_mode}
                            className="ml-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            Submit Payment
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Vendor Form Modal */}
            <Modal show={open} onClose={() => { setOpen(false); resetForm(); }}>
                <Modal.Header>{mode === "add" ? "Add Vendor" : "Edit Vendor"}</Modal.Header>
                <Modal.Body>
                    <form>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label>Vendor Name</Label>
                                <TextInput
                                    placeholder="Enter Vendor Name"
                                    value={vendor_name}
                                    onChange={(e) => {
                                        const validName = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                        setVendor_name(validName);
                                    }}
                                />
                            </div>
                            <div>
                                <Label>Mobile/WhatsApp No</Label>
                                <TextInput
                                    placeholder="Enter Mobile No"
                                    value={mobile_no}
                                    onChange={handleMobileNoChange}
                                />
                                {mobileNoWarning && (
                                    <p className={`text-sm ${mobileNoWarning.includes("Valid") ? "text-green-500" : "text-red-500"}`}>
                                        {mobileNoWarning}
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label>Email</Label>
                                <TextInput
                                    type="email"
                                    placeholder="Enter Email"
                                    value={email_id}
                                    onChange={(e) => setEmail_id(e.target.value)}
                                />
                            </div>
                            <div>
                                <Label>Address</Label>
                                <TextInput
                                    placeholder="Enter Address"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                />
                            </div>
                            <div>
                                <Label>GST No</Label>
                                <TextInput
                                    placeholder="Enter GST No"
                                    value={gst_no}
                                    onChange={(e) => {
                                        const value = e.target.value.toUpperCase();
                                        setGst_no(value);
                                        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9][A-Z][0-9A-Z]$/;
                                        if (value === "" || gstRegex.test(value)) {
                                            setError("");
                                        } else {
                                            setError("Invalid GST Number. Format: 22AAAAA0000A1Z5");
                                        }
                                    }}
                                />
                                {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
                            </div>
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button color="gray" onClick={() => { setOpen(false); resetForm(); }}>
                        Cancel
                    </Button>
                    <Button
                        disabled={
                            !vendor_name ||
                            !mobile_no ||
                            !address ||
                            (gst_no && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9][A-Z][0-9A-Z]$/.test(gst_no)) ||
                            (email_id && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email_id))
                        }
                        onClick={mode === "add" ? handlesubmit : handleedit}
                    >
                        {mode === "add" ? "Add" : "Update"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Payment Logs Modal */}
            <Modal show={isLogModalOpen} onClose={() => setIsLogModalOpen(false)} size="2xl">
                <Modal.Header>Payment Logs for Vendor</Modal.Header>
                <Modal.Body>
                    {logData.length > 0 ? (
                        <>
                            <div className="flex justify-end mb-4">
                                <Button onClick={handleDownloadLogPDF} color="success">
                                    Download PDF
                                </Button>
                            </div>
                            <table className="min-w-full text-sm text-left">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-3 px-4">S.No</th>
                                        <th className="py-3 px-4">Date</th>
                                        <th className="py-3 px-4">Amount</th>
                                        <th className="py-3 px-4">Payment Mode</th>
                                    </tr>
                                </thead>
                                <tbody>
                                {[...logData]
                                    .sort((a, b) => {
                                        const parseDate = (str) => {
                                            const [day, month, year] = str.split('-').map(Number);
                                            return new Date(year, month - 1, day);
                                        };
                                        return parseDate(b[1]) - parseDate(a[1]);
                                    })
                                    .map((log, index) => (
                                        <tr key={log[0] || index} className="border-b">
                                            <td className="py-2 px-4">{index + 1}</td>
                                            <td className="py-2 px-4">{log[1]}</td>
                                            <td className="py-2 px-4">{formatCurrency(log[2])}</td>
                                            <td className="py-2 px-4">{log[4]}</td>
                                        </tr>
                                    ))}
                            </tbody>
                            <tfoot className="bg-gray-100 font-bold">
                                <tr>
                                    <td colSpan="2" className="py-3 px-4 text-right">Total:</td>
                                    <td className="py-3 px-4">{formatCurrency(
                                        logData.reduce((sum, log) => {
                                            const amount = typeof log[2] === 'number' ? log[2] : parseFloat(log[2]);
                                            return sum + (isNaN(amount) ? 0 : amount);
                                        }, 0)
                                    )}</td>
                                    <td className="py-3 px-4"></td>
                                </tr>
                            </tfoot>
                        </table>
                        </>
                    ) : (
                        <p className="text-center text-gray-500 py-8">No payment logs found.</p>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Vendor;
